import { useFormik } from "formik";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Spinner } from "reactstrap";
import * as Yup from "yup";
import { ResetPasswordAPI } from "../../api/authAPIs";
import {
    Block,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockTitle,
    Button,
    PreviewCard,
} from "../../components/Component";
import LogoDark from "../../images/logo-dark.png";
import Logo from "../../images/logo.png";
import Head from "../../layout/head/Head";
import AuthFooter from "./AuthFooter";

toast.configure();

const ResetPassword = () => {
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            email: "",
            otp: "",
            newPassword: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Invalid email address").required("Email is required"),
            otp: Yup.number().required("OTP is required"),
            newPassword: Yup.string()
                .min(8, "Password must be at least 8 characters")
                .required("New Password is required"),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const response = await ResetPasswordAPI({ body: values })
                const result = response.data;
                toast.success(result.message, {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } catch (error) {
                toast.error("An error occurred. Please try again.", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <>
            <Head title="Reset-Password" />
            <Block className="nk-block-middle nk-auth-body wide-xs">
                <div className="brand-logo pb-4 text-center">
                    <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
                        <img className="logo-light logo-img logo-img-lg" src={Logo} alt="logo" />
                        <img className="logo-dark logo-img logo-img-lg" src={LogoDark} alt="logo-dark" />
                    </Link>
                </div>
                <PreviewCard className="card-bordered" bodyClass="card-inner-lg">
                    <BlockHead>
                        <BlockContent>
                            <BlockTitle tag="h5">Reset Your Password</BlockTitle>
                            <BlockDes>
                                <p>Enter your email, OTP, and a new password to reset your account password.</p>
                            </BlockDes>
                        </BlockContent>
                    </BlockHead>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="form-group">
                            <div className="form-label-group">
                                <label className="form-label" htmlFor="email">
                                    Email
                                </label>
                            </div>
                            <input
                                type="text"
                                className={`form-control form-control-lg ${formik.touched.email && formik.errors.email ? "is-invalid" : ""
                                    }`}
                                id="email"
                                placeholder="Enter your email address"
                                {...formik.getFieldProps("email")}
                            />
                            {formik.touched.email && formik.errors.email ? (
                                <div className="invalid-feedback">{formik.errors.email}</div>
                            ) : null}
                        </div>
                        <div className="form-group">
                            <div className="form-label-group">
                                <label className="form-label" htmlFor="otp">
                                    OTP
                                </label>
                            </div>
                            <input
                                type="number"
                                className={`form-control form-control-lg ${formik.touched.otp && formik.errors.otp ? "is-invalid" : ""
                                    }`}
                                id="otp"
                                placeholder="Enter the OTP"
                                {...formik.getFieldProps("otp")}
                            />
                            {formik.touched.otp && formik.errors.otp ? (
                                <div className="invalid-feedback">{formik.errors.otp}</div>
                            ) : null}
                        </div>
                        <div className="form-group">
                            <div className="form-label-group">
                                <label className="form-label" htmlFor="newPassword">
                                    New Password
                                </label>
                            </div>
                            <input
                                type="password"
                                className={`form-control form-control-lg ${formik.touched.newPassword && formik.errors.newPassword ? "is-invalid" : ""
                                    }`}
                                id="newPassword"
                                placeholder="Enter your new password"
                                {...formik.getFieldProps("newPassword")}
                            />
                            {formik.touched.newPassword && formik.errors.newPassword ? (
                                <div className="invalid-feedback">{formik.errors.newPassword}</div>
                            ) : null}
                        </div>
                        <div className="form-group">
                            <Button color="primary" size="lg" className="btn-block" type="submit" disabled={loading}>
                                {loading ? <Spinner size="sm" color="light" /> : "Reset Password"}
                            </Button>
                        </div>
                    </form>
                    <div className="form-note-s2 text-center pt-4">
                        <Link to={`${process.env.PUBLIC_URL}/auth-login`}>
                            <strong>Return to login</strong>
                        </Link>
                    </div>
                </PreviewCard>
            </Block>
            <AuthFooter />
        </>
    );
};

export default ResetPassword;
